import { useRef, useState } from "react"
import { css, cx } from "@emotion/css"
import { useSelect, UseSelectStateChange } from "downshift"

import ButtonBase from "@material-ui/core/ButtonBase"
import Popper from "@material-ui/core/Popper"
import { useI18n } from "providers/I18n"

const styles = {
  root: css`
    display: inline-block;
    line-height: 1rem;
  `,
  trigger: css`
    display: inline-flex;
    position: relative;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    border: 0;
    outline: 0;
    color: inherit;
    background-color: transparent;
    user-select: none;

    cursor: pointer;

    :disabled {
      cursor: default;
    }

    :focus:enabled {
      .dot {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px currentColor;
      }
    }
    :hover:enabled {
      .dot {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px currentColor;
      }
    }

    .dot {
      width: 15px;
      height: 15px;
      line-height: 1rem;
      border-radius: 50%;
      background-color: currentColor;
      margin-right: 20px;
      transition: box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }
  `,
  container: css`
    background-color: #fff;
    z-index: 42;
    position: relative;
    outline: none;
    min-width: 480px;
    width: min-content;
  `,
  menu: css`
    background-color: #fff;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.75rem;

    border-radius: 4px;
    box-shadow:
      0 2px 1px 0 rgba(0, 0, 0, 0.03),
      0 0 9px 0 rgba(0, 0, 0, 0.25);

    .menu-label {
      font-size: 0.875rem;
      line-height: 1rem;
      font-weight: 500;
      text-transform: capitalize;
      display: inline-flex;
    }

    > :not(:last-child) {
      margin-right: 1.5rem;
    }
  `,
  colorOptions: css`
    flex: 1;
    display: inline-flex;
    align-items: center;
  `,
  colorOption: css`
    cursor: pointer;
    padding: 8px;
    &:not(:last-child) {
      margin-right: 4px;
    }
  `,
  colorOptionDot: css`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #000;
    background-color: currentColor;
    display: flex;
    flex-shrink: 0;
    transition: box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  `,
  highlighted: css`
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 4px #00b1a4;
  `,

  reset: css`
    border-radius: 4px;
    padding: 4px 14px;
    font-size: 0.875rem;
    line-height: 1rem;
    color: #00b1a4;
    transition:
      background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    :focus:enabled {
      background-color: rgba(0, 177, 163, 0.04);
    }
    :hover:enabled {
      background-color: rgba(0, 177, 163, 0.04);
    }
    white-space: nowrap;
  `,
}

export type ColorPickerProps = {
  options: string[]
  color?: string
  defaultColor?: string
  onSelectedColorChange?: (color: string, id?: string) => void
  id?: string
}

export function ColorPicker(props: ColorPickerProps) {
  const {
    options,
    id,
    defaultColor = "#000",
    color: initialColor,
    onSelectedColorChange,
  } = props
  const { t } = useI18n()

  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [color, setColor] = useState<string>(() => initialColor || defaultColor)

  const handleSelectedItemChange = (changes: UseSelectStateChange<string>) => {
    const { selectedItem } = changes
    if (selectedItem) {
      setColor(selectedItem)
      requestAnimationFrame(() => {
        onSelectedColorChange && onSelectedColorChange(selectedItem, id)
      })
    }
  }

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    closeMenu,
  } = useSelect({
    items: options,
    selectedItem: color,
    onSelectedItemChange: handleSelectedItemChange,
  })

  const handleReset = () => {
    selectItem(defaultColor)
    requestAnimationFrame(() => {
      closeMenu()
    })
  }

  return (
    <div className={styles.root}>
      <label className="visually-hidden" {...getLabelProps()}>
        pick color
      </label>
      <button
        type="button"
        className={styles.trigger}
        style={{ color }}
        {...getToggleButtonProps({
          ref: triggerRef,
        })}
      >
        <div className="dot" />
      </button>
      <div {...getMenuProps({})}>
        <Popper
          open={isOpen}
          anchorEl={triggerRef.current}
          placement="bottom-start"
          className={styles.container}
          disablePortal={true}
        >
          <div className={styles.menu}>
            <label className="menu-label">color</label>
            <div className={styles.colorOptions}>
              {options.map((color, index) => {
                return (
                  <div
                    key={`${color}${index}`}
                    {...getItemProps({ item: color, index })}
                    className={cx("color-option", styles.colorOption)}
                  >
                    <div
                      className={cx(styles.colorOptionDot, {
                        [styles.highlighted]: highlightedIndex === index,
                      })}
                      style={{ color }}
                    />
                  </div>
                )
              })}
            </div>
            <div>
              <ButtonBase
                className={styles.reset}
                onClick={handleReset}
                aria-label="clear selection"
              >
                {t("admin.mediaTypes.color.reset")}
              </ButtonBase>
            </div>
          </div>
        </Popper>
      </div>
    </div>
  )
}
